<template>
  <div class="notification-fab" :style="{ '--notification-accent': accentColor }">
    <div v-if="policyError || !policyLoaded" role="status" class="notification-policy-status">
      {{ policyError || "通知展示策略加载中…" }}
    </div>
    <el-badge :value="unreadCount" :max="99" :hidden="unreadCount === 0">
      <el-button
        class="notification-fab__button"
        circle
        :icon="Bell"
        aria-label="通知中心"
        @click="toggle"
      />
    </el-badge>
  </div>

  <el-drawer
    v-model="drawerVisible"
    class="notification-drawer"
    direction="rtl"
    :modal="false"
    :size="380"
    :title="`通知中心 (${visibleItems.length} 条，${unreadCount} 未读)`"
  >
    <div class="notification-drawer__body">
      <div class="notification-filters">
        <el-select v-model="filters.level" size="small" clearable placeholder="级别">
          <el-option
            v-for="level in levelOptions"
            :key="level"
            :label="levelLabels[level]"
            :value="level"
          />
        </el-select>
        <el-select v-model="filters.source" size="small" clearable placeholder="来源">
          <el-option
            v-for="source in sourceOptions"
            :key="source"
            :label="source"
            :value="source"
          />
        </el-select>
        <el-select v-model="filters.fundId" size="small" clearable placeholder="基金">
          <el-option v-for="fund in fundOptions" :key="fund" :label="fund" :value="fund" />
        </el-select>
        <el-select v-model="filters.accountId" size="small" clearable placeholder="账户">
          <el-option
            v-for="account in accountOptions"
            :key="account"
            :label="account"
            :value="account"
          />
        </el-select>
      </div>

      <div ref="notificationList" class="notification-list" @scroll="handleListScroll">
        <el-card
          v-for="group in filteredItems"
          :key="group.key"
          class="notification-card"
          :class="{ 'is-expired': group.expired }"
          shadow="never"
          :body-style="{ padding: '0' }"
        >
          <div class="notification-card__inner">
            <div
              class="notification-card__level"
              :style="{ backgroundColor: levelColor(group.level) }"
            />
            <div class="notification-card__content">
              <div class="notification-card__header">
                <div class="notification-card__main">
                  <div class="notification-card__title-row">
                    <span class="notification-card__title">{{ group.title }}</span>
                    <span v-if="group.state === 'resolved'">已恢复</span>
                    <span class="notification-card__count">{{ group.count }} 条状态更新</span>
                  </div>
                  <div class="notification-card__time">
                    {{ formatNotificationTime(group.latestTs) }}
                  </div>
                </div>
                <el-button
                  text
                  size="small"
                  class="notification-card__copy"
                  :icon="CopyDocument"
                  @click="handleCopy(group)"
                >
                  复制
                </el-button>
                <el-button
                  text
                  size="small"
                  class="notification-card__mute"
                  @click="mute(group.key)"
                >
                  静默
                </el-button>
              </div>

              <div class="notification-card__body">{{ group.body }}</div>

              <div v-if="group.actions?.length" class="notification-card__actions">
                <a
                  v-for="action in group.actions"
                  :key="`${group.key}-${action.url}`"
                  class="notification-card__action"
                  :href="action.url"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {{ action.label }}
                </a>
              </div>
            </div>
          </div>
        </el-card>
        <div v-if="!filteredItems.length" class="notification-empty">暂无通知</div>
        <div class="notification-history-status" aria-live="polite">
          <span v-if="historyLoading">正在加载更早通知…</span>
          <span v-else-if="!historyHasMore && oldestHistoryTs !== null"> 已加载全部历史通知 </span>
        </div>
      </div>

      <div class="notification-footer">
        <el-button size="small" text :disabled="filteredItems.length === 0" @click="muteFiltered">
          全部静默
        </el-button>
        <el-button size="small" text @click="clearRead">清空已读</el-button>
      </div>
    </div>
  </el-drawer>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { ElMessage } from "element-plus";
import { Bell, CopyDocument } from "@element-plus/icons-vue";
import { useNotificationBus } from "@/composables/useNotificationBus";
import { useNotificationSocket } from "@/composables/useNotificationSocket";
import type { NotificationGroup, NotificationLevel } from "@/composables/useNotificationBus";
import { buildNotificationCopyText, formatNotificationTime } from "@/utils/notificationCenter";
import { copyText } from "@/utils/clipboard";
import { useNotificationDisplayPolicy } from "@/composables/useNotificationDisplayPolicy";

const { error: policyError, loaded: policyLoaded } = useNotificationDisplayPolicy();

const {
  visibleItems,
  filteredItems,
  unreadCount,
  highestLevel,
  drawerVisible,
  filters,
  sourceOptions,
  fundOptions,
  accountOptions,
  levelOptions,
  oldestHistoryTs,
  historyLoading,
  historyHasMore,
  realtimeVersion,
  toggle,
  mute,
  clearRead,
} = useNotificationBus();
const { loadMore } = useNotificationSocket();
const notificationList = ref<HTMLElement | null>(null);

const levelLabels: Record<NotificationLevel, string> = {
  info: "信息",
  warning: "警告",
  error: "错误",
  critical: "严重",
};

const accentColor = computed(() => levelColor(highestLevel.value));

function handleListScroll(): void {
  const list = notificationList.value;
  if (!list) return;
  if (list.scrollHeight - list.scrollTop - list.clientHeight <= 32) loadMore();
}

watch(realtimeVersion, async () => {
  const list = notificationList.value;
  if (!list || list.scrollTop <= 8) return;
  const previousTop = list.scrollTop;
  const previousHeight = list.scrollHeight;
  await nextTick();
  list.scrollTop = previousTop + (list.scrollHeight - previousHeight);
});

watch([drawerVisible, filteredItems], async ([visible]) => {
  if (!visible) return;
  await nextTick();
  const list = notificationList.value;
  if (list && list.scrollHeight <= list.clientHeight + 1) loadMore();
});

function levelColor(level: NotificationLevel): string {
  if (level === "warning") return "#E6A23C";
  if (level === "error" || level === "critical") return "#F56C6C";
  return "#909399";
}

function muteFiltered(): void {
  for (const group of filteredItems.value) {
    mute(group.key);
  }
}

async function handleCopy(group: NotificationGroup): Promise<void> {
  const ok = await copyText(buildNotificationCopyText(group));
  if (ok) {
    ElMessage.success("通知已复制到剪贴板");
  } else {
    ElMessage.error("复制失败，请手动选中文本");
  }
}
</script>

<style scoped>
.notification-fab {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 1800;
}

.notification-policy-status {
  max-width: 290px;
  margin-bottom: 8px;
  padding: 8px;
  background: #fdf6ec;
  border: 1px solid #e6a23c;
  color: #9b651c;
  font-size: 12px;
}

.notification-fab__button {
  width: 44px;
  height: 44px;
  color: var(--notification-accent);
  border-color: var(--notification-accent);
  background: #fff;
  box-shadow: 0 6px 18px rgb(0 0 0 / 14%);
}

.notification-fab__button:hover,
.notification-fab__button:focus {
  color: #fff;
  border-color: var(--notification-accent);
  background: var(--notification-accent);
}

.notification-fab :deep(.el-badge__content) {
  background-color: var(--notification-accent);
  border-color: var(--notification-accent);
}

:global(.notification-drawer .el-drawer__body) {
  padding: 12px;
  overflow: hidden;
}

.notification-drawer__body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
  min-height: 0;
}

.notification-filters {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.notification-list {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
  padding-right: 2px;
  overflow-y: auto;
}

.notification-card {
  flex: 0 0 auto;
  border-radius: 8px;
  overflow: hidden;
}

.notification-card.is-expired {
  opacity: 0.5;
}

.notification-card__inner {
  display: flex;
  min-height: 112px;
}

.notification-card__level {
  flex: 0 0 4px;
}

.notification-card__content {
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
}

.notification-card__header {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.notification-card__main {
  flex: 1;
  min-width: 0;
}

.notification-card__title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.notification-card__title {
  min-width: 0;
  overflow: hidden;
  color: var(--el-text-color-primary);
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notification-card__count {
  flex: 0 0 auto;
}

.notification-card__time {
  margin-top: 2px;
  color: var(--el-text-color-secondary);
  font-size: 12px;
  line-height: 18px;
}

.notification-card__copy,
.notification-card__mute {
  flex: 0 0 auto;
  padding: 0;
}

.notification-card__body {
  max-height: 112px;
  margin-top: 8px;
  overflow-y: auto;
  color: var(--el-text-color-regular);
  font-size: 13px;
  line-height: 19px;
  white-space: pre-wrap;
  word-break: break-word;
}

.notification-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.notification-card__action {
  color: var(--el-color-primary);
  font-size: 13px;
  line-height: 20px;
  text-decoration: none;
}

.notification-card__action:hover {
  text-decoration: underline;
}

.notification-empty {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  min-height: 180px;
  color: var(--el-text-color-secondary);
  font-size: 14px;
}

.notification-history-status {
  flex: 0 0 auto;
  min-height: 20px;
  color: var(--el-text-color-secondary);
  font-size: 12px;
  line-height: 20px;
  text-align: center;
}

.notification-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--el-border-color-lighter);
}
</style>
